import React, { useState } from 'react';
import { formatCallSlot, loadCallWindow } from '../lib/callSlots';

/**
 * Choix du créneau après le paiement (/v60). Mêmes créneaux que sur les autres pages (lib/callSlots.js),
 * envoyés au Worker (POST /api/calls/book), qui les écrit sur la session Stripe et prévient Gaut.
 */
export default function CallPicker({ sessionId, lang, onBooked }) {
  const en = lang === 'en';
  const [window0] = useState(() => loadCallWindow());
  const slots = window0.slots.map((iso) => formatCallSlot(iso, lang));
  const [picked, setPicked] = useState(window0.selected || slots[0]?.iso);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const book = async () => {
    const slot = slots.find((item) => item.iso === picked);
    if (!slot || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/calls/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          call_at: slot.iso,
          call_label: slot.label,
          call_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: en ? 'en' : 'fr',
        }),
        signal: AbortSignal.timeout(8000),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.confirmed || data.call_label)) onBooked(data.call_label || slot.label);
      else setError(data.detail || (en ? 'Booking failed. Try again.' : 'La réservation n’a pas marché. Réessaie.'));
    } catch {
      setError(en ? 'Connection problem. Try again.' : 'Problème de connexion. Réessaie.');
    }
    setBusy(false);
  };

  return (
    <div className="mb-6 rounded-xl p-5 text-left" style={{ backgroundColor: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
      <p className="text-[12px] font-bold uppercase tracking-wider mb-1" style={{ color: '#00D4FF', fontFamily: '"Inter", sans-serif' }}>
        {en ? 'Your first call' : 'Ton premier appel'}
      </p>
      <p className="text-[15px] text-white mb-4" style={{ fontFamily: '"Inter", sans-serif' }}>
        {en ? 'Pick your time. Gaut calls you on the number you gave at checkout.' : 'Choisis ton créneau. Gaut t’appelle au numéro indiqué au paiement.'}
        <br /><span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
          {en ? 'Optional: you can skip this and book later by emailing mia@openpro.ai.' : 'Facultatif : tu peux passer cette étape et réserver plus tard en écrivant à mia@openpro.ai.'}
        </span>
      </p>
      <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup" aria-label={en ? 'Open slots' : 'Créneaux disponibles'}>
        {slots.map((slot) => {
          const on = slot.iso === picked;
          return (
            <button
              key={slot.iso}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setPicked(slot.iso)}
              className="rounded-lg py-3 text-[14px] font-bold transition-colors"
              style={{ backgroundColor: on ? '#00D4FF' : 'rgba(255,255,255,0.04)', color: on ? '#000' : '#fff', border: `1px solid ${on ? '#00D4FF' : 'rgba(255,255,255,0.15)'}`, fontFamily: '"Inter", sans-serif' }}
            >
              {slot.day}<br /><span className="font-normal">{slot.time}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="text-[13px] mb-3" style={{ color: '#f87171', fontFamily: '"Inter", sans-serif' }}>{error}</p>}
      <button
        type="button"
        onClick={book}
        disabled={busy || !picked}
        data-testid="book-call"
        className="w-full py-3 rounded-lg font-bold text-[15px] disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)', color: '#000', fontFamily: '"Inter", sans-serif' }}
      >
        {busy ? '…' : (en ? 'Book this time' : 'Réserver ce créneau')}
      </button>
    </div>
  );
}
